import { TranslationType } from '../components/locales/types';
import { API_BASE_URL, API_ENDPOINTS } from 'src/resources/api-constants';

// List of supported languages if backend fails
const FALLBACK_LANGUAGES = ['en', 'es', 'fr', 'cn', 'tw'];
const LANGUAGE_COOKIE_NAME = 'preferred_language';

/**
 * Gets the current language from cookies if available
 * @returns The current language code from cookies, or null if not set
 */
export function getCurrentLanguageFromCookies(): string | null {
  try {
    // Parse cookies
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [name, value] = cookie.trim().split('=');
      acc[name] = value;
      return acc;
    }, {} as Record<string, string>);
    
    // Check if language cookie exists
    const langCookie = cookies[LANGUAGE_COOKIE_NAME];
    if (langCookie) {
      console.log(`Found language cookie: ${langCookie}`);
      return langCookie;
    }
    
    console.log('No language cookie found');
    return null;
  } catch (error) {
    console.error('Error reading language cookie:', error);
    return null;
  }
}

/**
 * Fetches navigation translations from the backend API.
 * The API uses cookies for language preferences.
 * 
 * @returns A Promise that resolves to the translation data
 */
export async function fetchNavigationTranslations(): Promise<TranslationType> {
  try {
    console.log('Fetching translations from backend...');
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.NAVIGATION.BASE}`, {
      method: 'GET',
      credentials: 'include' // Important: Include cookies in the request
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch navigation data: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Received translations:', data);
    
    // Validate that we got a proper structure
    if (!data || !data.navigations) {
      console.error('Invalid translation data structure:', data);
      return createEmptyTranslations();
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching navigation translations:', error);
    // Return empty structure as fallback
    return createEmptyTranslations();
  }
}

/**
 * Fetches navigation translations for a specific language from the backend API.
 * This also sets the language preference cookie.
 * 
 * @param language The language code (e.g., 'en', 'es', 'fr', 'cn', 'tw')
 * @returns A Promise that resolves to the translation data
 */
export async function fetchNavigationTranslationsForLanguage(language: string): Promise<TranslationType> {
  try {
    console.log(`Fetching translations for language: ${language}`);
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.NAVIGATION.BY_LANGUAGE(language)}`, {
      method: 'GET',
      credentials: 'include' // Important: Include cookies in the request
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch navigation data for language '${language}': ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`Received translations for ${language}:`, data);
    
    // Validate that we got a proper structure
    if (!data || !data.navigations) {
      console.error(`Invalid translation data structure for ${language}:`, data);
      return createEmptyTranslations();
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching navigation translations for language '${language}':`, error);
    // Return empty structure as fallback
    return createEmptyTranslations();
  }
}

/**
 * Sets the preferred language via API and cookie.
 * 
 * @param language The language code to set as preferred
 * @returns A Promise that resolves to a success/error status
 */
export async function setPreferredLanguage(language: string): Promise<{ status: string; language?: string; error?: string; message?: string }> {
  try {
    console.log(`Setting preferred language to: ${language}`);
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.NAVIGATION.SET_LANGUAGE(language)}`, {
      method: 'POST',
      credentials: 'include', // Important: Include cookies in the request
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error(`Error response from language endpoint: ${response.status} ${response.statusText}`);
      return { 
        status: 'error', 
        message: `Server returned ${response.status}: ${response.statusText}` 
      };
    }
    
    const result = await response.json();
    console.log(`Server response for language change to ${language}:`, result);
    return result;
  } catch (error) {
    console.error('Error setting preferred language:', error);
    return { 
      status: 'error', 
      message: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Gets the list of supported languages from the API.
 * 
 * @returns A Promise that resolves to an object with supported languages and default language
 */
export async function getSupportedLanguages(): Promise<{ supported: string[]; default: string }> {
  try {
    console.log('Fetching supported languages from backend...');
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.NAVIGATION.LANGUAGES}`, {
      method: 'GET',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch supported languages: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Received supported languages:', data);
    
    // Validate the response
    if (!data || !Array.isArray(data.supported) || !data.supported.length) {
      console.error('Invalid or empty supported languages:', data);
      return { supported: FALLBACK_LANGUAGES, default: 'en' };
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching supported languages:', error);
    // If API call fails, return the hardcoded list
    return { supported: FALLBACK_LANGUAGES, default: 'en' };
  }
}

/**
 * Creates an empty translations object for fallback.
 */
function createEmptyTranslations(): TranslationType {
  return {
    navigations: {
      header: {
        home: '',
        news: '',
        entertainment: '',
        sports: '',
        signIn: ''
      },
      footer: {
        about: '',
        contact: '',
        privacy: '',
        terms: '',
        copyright: '',
        followUs: '',
        subscribe: ''
      },
      home: {
        title: ''
      },
      about: {
        title: '',
        description: ''
      },
      notFound: {
        title: '',
        subtitle: '',
        homeLink: ''
      }
    }
  };
} 